import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { BOARD_KEYS } from '@/features/boards/public';
import { tasksService } from '../api/tasks.service';
import { TASK_KEYS } from './queryKeys';
import { useProjectStatuses } from './useProjectStatuses';
import { updateTaskEntity } from './useTaskEntityCache';
import type { TaskGroupPage } from './useTaskGroupInfinite';
import type { Task } from '../types';

export function useTaskBoard(
  projectId: string,
  boardId: string,
  serializedFilters?: string,
  search?: string,
  serializedSort?: string,
) {
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);
  const queryClient = useQueryClient();
  const { statuses, isLoading: statusesLoading } = useProjectStatuses(boardId);

  function getColumnCacheKey(statusId: string) {
    return TASK_KEYS.groupPage(
      orgId ?? '',
      projectId,
      'status',
      statusId,
      boardId,
      serializedFilters,
      search,
      serializedSort,
    );
  }

  function getColumnTasks(statusId: string): Task[] {
    const data = queryClient.getQueryData<InfiniteData<TaskGroupPage>>(getColumnCacheKey(statusId));
    return data?.pages.flatMap((p) => p.items) ?? [];
  }

  const { mutate: doMoveTask } = useMutation({
    mutationFn: ({
      taskId,
      statusId,
      groupId,
      afterTaskId,
    }: {
      taskId: string;
      statusId: string;
      groupId?: string;
      afterTaskId?: string | null;
    }) =>
      tasksService.moveTask(taskId, {
        statusId,
        groupId: groupId ?? null,
        afterTaskId: afterTaskId ?? null,
      }),

    onMutate: async ({ taskId, statusId }) => {
      const status = statuses.find((s) => s.id === statusId);
      if (status) {
        updateTaskEntity(queryClient, orgId ?? '', taskId, (task) => ({ ...task, status }));
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.projectRoot(orgId ?? '', projectId) });
    },
  });

  function isColumnFullyLoaded(statusId: string): boolean {
    const data = queryClient.getQueryData<InfiniteData<TaskGroupPage>>(getColumnCacheKey(statusId));
    const loaded = data?.pages.flatMap((p) => p.items).length ?? 0;
    const total = data?.pages[0]?.count ?? 0;
    return loaded >= total;
  }

  function moveTask(taskId: string, targetStatusId: string, afterTaskId: string | null) {
    doMoveTask({ taskId, statusId: targetStatusId, afterTaskId });
  }

  const { mutate: doReorderTasks } = useMutation({
    mutationFn: (payload: { boardId: string; statusId: string; orderedTaskIds: string[] }) =>
      tasksService.reorderTasks({
        boardId: payload.boardId,
        statusId: payload.statusId,
        orderedTaskIds: payload.orderedTaskIds,
      }),

    onMutate: async ({ statusId, orderedTaskIds }) => {
      const columnKey = getColumnCacheKey(statusId);
      await queryClient.cancelQueries({ queryKey: columnKey });
      const snapshot = queryClient.getQueryData(columnKey);

      queryClient.setQueryData<InfiniteData<TaskGroupPage>>(columnKey, (old) => {
        if (!old) return old;
        const allItems = old.pages.flatMap((p) => p.items);
        const idToTask = new Map(allItems.map((t) => [t.id, t]));
        const reordered = orderedTaskIds
          .map((id) => idToTask.get(id))
          .filter((t): t is Task => !!t);
        return {
          ...old,
          pages: old.pages.map((p, i) =>
            i === 0 ? { ...p, items: reordered } : { ...p, items: [] },
          ),
        };
      });

      return { snapshot, columnKey };
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot && ctx.columnKey) {
        queryClient.setQueryData(ctx.columnKey, ctx.snapshot);
      }
    },

    onSettled: (_data, _err, { statusId }) => {
      queryClient.invalidateQueries({ queryKey: getColumnCacheKey(statusId) });
    },
  });

  function reorderTask(taskId: string, statusId: string, targetIndex: number) {
    const columnTasks = getColumnTasks(statusId);
    const others = columnTasks.filter((t) => t.id !== taskId).map((t) => t.id);
    others.splice(targetIndex, 0, taskId);
    doReorderTasks({ boardId, statusId, orderedTaskIds: others });
  }

  const { mutate: doCreateTask } = useMutation({
    mutationFn: ({ title, statusId }: { title: string; statusId: string }) =>
      tasksService.create(projectId, { title, boardId, statusId }),

    onMutate: async ({ title, statusId }) => {
      const columnKey = getColumnCacheKey(statusId);
      await queryClient.cancelQueries({ queryKey: columnKey });
      const snapshot = queryClient.getQueryData(columnKey);
      const status = statuses.find((s) => s.id === statusId);
      if (status) {
        const optimistic: Task = {
          id: `optimistic-${Date.now()}`,
          title,
          status,
          assignee: null,
          subtaskCount: 0,
          projectId,
          isCompleted: false,
          order: 0,
        };
        queryClient.setQueryData<InfiniteData<TaskGroupPage>>(columnKey, (old) => {
          if (!old) return old;
          const firstPage = old.pages[0];
          return {
            ...old,
            pages: [
              {
                ...firstPage,
                items: [...(firstPage?.items ?? []), optimistic],
                count: (firstPage?.count ?? 0) + 1,
              },
              ...old.pages.slice(1),
            ],
          };
        });
      }
      return { snapshot, columnKey };
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot && ctx.columnKey) {
        queryClient.setQueryData(ctx.columnKey, ctx.snapshot);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.projectRoot(orgId ?? '', projectId) });
    },
  });

  function createTask(title: string, statusId: string) {
    doCreateTask({ title, statusId });
  }

  function addStatusOptimistic() {
    queryClient.invalidateQueries({ queryKey: BOARD_KEYS.statuses(orgId ?? '', boardId) });
  }

  return {
    statuses,
    isLoading: statusesLoading,
    moveTask,
    reorderTask,
    createTask,
    addStatusOptimistic,
    isColumnFullyLoaded,
  };
}
